import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { GlobalSearch } from 'common/header/GlobalSearch/GlobalSearch';
import { IconButton } from '@beeline.com/bp-design-system';
import { Menu } from '../menu/Menu';
import { useScreenSizes } from '../useScreenSizes';
import { DesktopLogos } from './DesktopLogos';
import { HeaderActions } from './HeaderActions';
import { AuthUser } from 'src/providers/User/AuthUserModels';
import { AuthSessionInfo } from 'common/header/AuthSessionInfo';

const isDev = process.env.NODE_ENV === 'development';

interface Props {
  user: AuthUser;
  isHelpOpen: boolean;
  onHelpClick: () => void;
  onBriefingClick: () => void;
}

export const TopBar: FC<React.PropsWithChildren<Props>> = ({ user, isHelpOpen, onHelpClick, onBriefingClick }) => {
  const [isSearchVisible, setSearchVisibility] = useState(false);
  const history = useHistory();
  const { isSmallMobileScreen, isAnyMobileScreen, isLarge, isTabletScreen, isExtraLarge, isOnlyMobileScreen } =
    useScreenSizes();

  const showSearchInput = !isAnyMobileScreen || (isAnyMobileScreen && isSearchVisible);
  const showSearchIcon = isAnyMobileScreen && !isSearchVisible;
  const showDesktopsLogo = isOnlyMobileScreen || isTabletScreen || isLarge || isExtraLarge;
  const showHomeIcon = isSmallMobileScreen;
  const showMobileMenu = isAnyMobileScreen || isTabletScreen;
  const showTokenInfo = isDev && (isLarge || isExtraLarge);

  const goToHomePage = () => history.push('/');

  return (
    <StyledContainer data-test="app_banner">
      <StyledLeftContainer>
        {showMobileMenu && <Menu isMobileVersion showMobileLogo={!showDesktopsLogo} user={user} />}
        {showHomeIcon && <IconButton color="colorOnBanner" iconName="home" onClick={goToHomePage} />}{' '}
        {showDesktopsLogo && <DesktopLogos />}
        {showSearchInput && (
          <StyledSearchInputWrapper $isAnyMobileScreen={isAnyMobileScreen}>
            {isAnyMobileScreen && (
              <IconButton
                // className="x-close"
                onClick={() => setSearchVisibility((prevState) => !prevState)}
                iconName="arrow_back"
                aria-label="close-search"
              />
            )}
            <GlobalSearch />
          </StyledSearchInputWrapper>
        )}
      </StyledLeftContainer>
      <StyledRightContainer>
        {showSearchIcon && (
          <IconButton
            color="colorOnBanner"
            iconName="search"
            onClick={() => setSearchVisibility((prevState) => !prevState)}
          />
        )}
        {showTokenInfo && <AuthSessionInfo />}
        <HeaderActions isHelpOpen={isHelpOpen} onHelpClick={onHelpClick} onBriefingClick={onBriefingClick} />
      </StyledRightContainer>
    </StyledContainer>
  );
};

const StyledLeftContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacingSize.s};
`;

const StyledRightContainer = styled.div`
  gap: ${({ theme }) => theme.spacingSize.m};
  align-items: center;
  justify-content: flex-start;
  display: flex;
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  gap: ${({ theme }) => theme.spacingSize.m};
  padding: 0 ${({ theme }) => theme.spacingSize.l};
  width: 100%;
  background: ${(e) => e.theme.colorBanner};
  color: ${(e) => e.theme.colorOnBanner};
`;

const StyledSearchInputWrapper = styled.div<{ $isAnyMobileScreen: boolean }>`
  width: ${({ $isAnyMobileScreen }) => ($isAnyMobileScreen ? '100%' : '250px')};
  position: ${({ $isAnyMobileScreen }) => ($isAnyMobileScreen ? 'absolute' : 'inherit')};
  height: ${({ $isAnyMobileScreen }) => ($isAnyMobileScreen ? '100%' : 'auto')};
  background: ${({ theme }) => theme.colorBackground};
  left: 0;
  z-index: 100;
  top: 0;
  ${({ $isAnyMobileScreen }) =>
    $isAnyMobileScreen
      ? css`
          align-content: center;
          justify-content: center;
          display: grid;
          grid-template-columns: auto 1fr;
          padding: ${({ theme }) => theme.spacingSize.m};
          gap: ${({ theme }) => theme.spacingSize.m};
        `
      : ''};
`;
